import { useEffect, useRef, useState } from 'react'

import {
  CancelIcon,
  CheckedCircleIconIcon,
  GiftIcon,
} from '@twistopayments/design-system/icons'

import type {
  PlanFeature,
  SectionsPlanComponent,
  SectionsPlanFeatureComponent,
} from '@twisto/api-cms'

import * as styles from './pricing-table-mobile.styles'
import * as desktopStyles from './pricing-table.styles'

type PricingTableMobileProps = {
  plans: SectionsPlanComponent[]
  features?: SectionsPlanFeatureComponent[]
}

export const PricingTableMobile = ({
  plans,
  features,
}: PricingTableMobileProps) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const handleDotClick = (index: number) => {
    if (!scrollContainerRef.current) {
      return
    }

    const container = scrollContainerRef.current
    const scrollAmount = container.scrollWidth / plans.length

    container.scrollTo({ left: index * scrollAmount, behavior: 'smooth' })
  }

  useEffect(() => {
    const handleScroll = () => {
      if (!scrollContainerRef.current) {
        return
      }

      const container = scrollContainerRef.current
      const scrollAmount = container.scrollWidth / plans.length
      const newIndex = Math.round(container.scrollLeft / scrollAmount)

      setActiveIndex(newIndex)
    }

    const container = scrollContainerRef.current
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true })
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll)
      }
    }
  }, [plans.length])

  return (
    <>
      <div ref={scrollContainerRef} css={styles.mobile}>
        {plans.map(
          ({
            name,
            price,
            description,
            extra,
            gift,
            id,
            plan_features: planFeatures,
          }) => (
            <div
              key={id}
              css={[
                styles.paper,
                extra
                  ? [
                      desktopStyles.highlightedColumn,
                      desktopStyles.highlightedColumnLast,
                    ]
                  : '',
              ]}
            >
              {extra && <div css={desktopStyles.extra}>{extra}</div>}
              {gift && (
                <div css={desktopStyles.gift}>
                  <GiftIcon size="24" />
                  <p>{gift}</p>
                </div>
              )}
              <h3 css={styles.planName}>{name}</h3>
              <p css={styles.planPrice}>{price}</p>
              <p css={styles.planDescription}>{description}</p>
              <ul css={styles.features}>
                {features?.map(({ id, feature }) => {
                  const featureData = feature

                  if (!featureData) {
                    return null
                  }

                  const { name } = featureData as PlanFeature
                  const isChecked = planFeatures?.some(
                    ({ id: featureId }) => featureId === featureData.id
                  )

                  return (
                    <li key={id} css={styles.feature}>
                      <div css={isChecked ? desktopStyles.checkmarkIcon : ''}>
                        {isChecked ? (
                          <CheckedCircleIconIcon size="24" />
                        ) : (
                          <CancelIcon size="24" />
                        )}
                      </div>
                      <p css={styles.featureName}>{name}</p>
                    </li>
                  )
                })}
              </ul>
            </div>
          )
        )}
      </div>
      <div css={styles.dots}>
        {plans.map((_, index) => (
          <button
            key={index}
            css={[styles.dot, activeIndex === index && styles.activeDot]}
            onClick={() => handleDotClick(index)}
          />
        ))}
      </div>
    </>
  )
}
